import { useCallback, useContext } from 'react';
import { useQueryClient } from '@zg-rentals/react-query5';
import type { SatelliteConversationFolderTypeV2 } from '@zg-rentals/ts-satellite-client';
import { InboxPageContext } from '../contexts/InboxPageContextProvider';
import {
  buildRequestParams,
  getInboxConversationsQueryKey,
  type InboxConversation,
  type InboxConversationsInfiniteData,
} from './useInboxConversationsQuery';

export default () => {
  const queryClient = useQueryClient();
  const { clientTimeZone, daysBack, folder, isByProperty, listingAlias, readRepliedStatus, renterProgressStatus } =
    useContext(InboxPageContext);

  const removeConversationFromList = useCallback(
    (
      conversationId?: string,
      folderToRemoveConversationFrom?: SatelliteConversationFolderTypeV2,
    ): { conversations: Array<InboxConversation>; updatedConversations: Array<InboxConversation> } => {
      const params = buildRequestParams({
        clientTimeZone,
        daysBack,
        folder: folderToRemoveConversationFrom || folder,
        listingAlias: isByProperty ? listingAlias : null,
        readRepliedStatus,
        renterProgressStatus,
      });
      const queryKey = getInboxConversationsQueryKey(params);

      let allConversations: Array<InboxConversation> = [];

      const existingData = queryClient.getQueryData<InboxConversationsInfiniteData>(queryKey);
      if (existingData) {
        allConversations = existingData.pages.flatMap((page) => page.conversations);
      }

      const updatedConversations = allConversations.filter(
        (conversation) => conversation.conversationId !== conversationId,
      );

      if (existingData) {
        queryClient.setQueryData<InboxConversationsInfiniteData>(queryKey, {
          ...existingData,
          pages: [
            {
              conversations: updatedConversations,
              hasNextPage: existingData.pages[existingData.pages.length - 1]?.hasNextPage ?? false,
            },
          ],
          pageParams: [existingData.pageParams[0]],
        });
      }

      return {
        conversations: allConversations,
        updatedConversations,
      };
    },
    [clientTimeZone, daysBack, folder, isByProperty, listingAlias, readRepliedStatus, renterProgressStatus],
  );

  const getConversationToRedirectToNext = useCallback(
    (currentConversationId: string, conversations: Array<InboxConversation>) => {
      let nextConversationId = '';
      let nextListingAlias = '';

      if (conversations.length > 1) {
        const currentConversationIndex = conversations.findIndex(
          (conversation) => conversation.conversationId === currentConversationId,
        );

        if (currentConversationIndex !== -1) {
          const nextConversationIndex = (currentConversationIndex + 1) % conversations.length;
          nextConversationId = conversations[nextConversationIndex].conversationId;
          nextListingAlias = conversations[nextConversationIndex].listingDetails?.listingAlias ?? '';
        } else {
          nextConversationId = conversations[0].conversationId;
          nextListingAlias = conversations[0].listingDetails?.listingAlias ?? '';
        }
      }

      return {
        nextConversationId,
        nextListingAlias,
      };
    },
    [],
  );

  return {
    getConversationToRedirectToNext,
    removeConversationFromList,
  };
};
